import React, { useState} from 'react';
import { Panel } from '@appkit4/react-components/panel';
import MainStructure from '../components/layout/MainStructure/MainStructure';
import PanelSample from '../components/features/TitlePanel/TitlePanel';
import AccordionSample from '../components/common/Accordion/Accordion';
import ButtonLoadSubmit from '../components/common/Button/ButtonSubmit';
import axios from 'axios';
import UploadSample from '../components/features/UploadForm/UploadForm';
import { config } from '../config/env.config';
import { useToast } from '../hooks/useToast';
import { apiClient } from '../services/apiClient';
import { downloadFile, getSignedUrl, uploadFile as uploadFileUtil } from '../utils/fileUpload';
import { sanitizeUrl } from '../utils/url';

//VAR
const P7M_ENDPOINT = config.api.endpoints.process_p7m

//ELEMENTS
const accordionItems = [
  {
    title: "Come funziona?",
    images: [
      {
        url: "/zip_file_brand.png",
        subtitle: "Step 1",
        variableSubtitle: "Seleziona il file .zip e clicca il tasto upload."
      },
      {
        url: "/fill_file_brand.png",
        subtitle: "Step 2",
        variableSubtitle: "Verifica che il file sia stato caricato correttamente. Clicca sul pulsante \"Converti File\"."
      },
      {
        url: "/decrypt_file_brand.png",
        subtitle: "Step 3",
        variableSubtitle: "Attendi la comparsa del tasto 'Download' e scarica il file zip convertito. In caso di errori, appare un tasto per scaricare un file .txt"
      }
    ]
  }
];

const panelConfig = {
  title: "Convertitore P7M",
  brightnessOnHover: 90,
  imageUrl: 'suddivisione-pdf-invoice.png'
}

interface CustomRequestBody {
  filename: string;
}


const P7mPage = () => {
  // Stati per gestire vari aspetti dell'interfaccia utente e dei dati
  const [file, setFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [inputFileName, setInputFileName] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  const [errorFiles, setErrorFiles] = useState<string[]>([]);
  const { showError, showWarning } = useToast();


  // Gestione del cambiamento di file
  const handleFileChange = (newFile: File | null) => {
    setFile(newFile);
    if (newFile) {
      const safeName = newFile.name.split(/[\\/]/).pop() || newFile.name;
      setInputFileName(safeName);
      setDownloadUrl(''); // Reset URL di download
      setErrorFiles([]); // Cancella eventuali errori precedenti
      setIsDownloading(false); // Reset dello stato di download
    }
  };

  const handleFileOperations = async () => {
    if (!file) {
      showWarning('Seleziona un file prima di procedere.', 'Attenzione', { duration: 50000, clickToClose: true });
      setIsLoading(false);
      return;
    }
    setErrorFiles([]);
    setIsLoading(true);

    try {
      const signedUrl = await getSignedUrl(
        inputFileName,
        'application/zip',
        config.storage.p7mBucketName
      );
      await uploadFileUtil(signedUrl, file!, 'application/zip');

      const processResponse = await processFile(
        {
          filename: inputFileName
        });
        
      if (!processResponse) {
        throw new Error("File processing failed.");
      }

      } catch (error) {
        console.error("Operation failed");
        
        if (axios.isAxiosError(error)) {
          if (error.response?.status === 400) {
            const errorData = error.response?.data;
            const errorMessage = errorData?.message || '';
            
            // Invalid ZIP file error
            if (errorMessage.includes('invalid .zip file')) {
              showWarning(
                'Il file caricato non è un file ZIP valido.',
                'File Non Valido',
                { duration: 50000, clickToClose: true }
              );
              return;
            }
            
            // Empty ZIP error
            if (errorMessage.includes('Empty ZIP')) {
              showWarning(
                'Il file ZIP è vuoto o non contiene file .p7m validi.',
                'ZIP Vuoto',
                { duration: 50000, clickToClose: true }
              );
              return;
            }
            
            // Non-p7m files error with file list
            if (errorMessage.includes('.zip must contain only .p7m files') || 
                errorMessage.includes('.zip must contains only .p7m files')) {
              const errors = errorData?.content?.errors || [];
              if (errors.length > 0) {
                setErrorFiles(errors);
                showWarning(
                  `Il file ZIP deve contenere solo file .p7m. Trovati ${errors.length} file non validi.`,
                  'File Non Validi',
                  { duration: 50000, clickToClose: true }
                );
              } else {
                showWarning(
                  'Il file ZIP deve contenere solo file .p7m.',
                  'File Non Validi',
                  { duration: 50000, clickToClose: true }
                );
              }
              return;
            }
            
            // Unsupported file types
            if (errorMessage.includes('must contain only .pdf or .xml files')) {
              const errors = errorData?.content?.errors || [];
              setErrorFiles(errors);
              showWarning(
                `Il file ZIP deve contenere solo file .pdf o .xml. Trovati ${errors.length} file non supportati.`,
                'File Non Supportati',
                { duration: 50000, clickToClose: true }
              );
              return;
            }
            
            // Generic 400 error
            showError('Errore nella richiesta di elaborazione del file.', "", { duration: 50000, clickToClose: true });
          } else if (error.code === 'ERR_NETWORK') {
            showError('Errore di connessione al server.', "", { duration: 50000, clickToClose: true });
          } else {
            showError('Si è verificato un errore durante l\'elaborazione del file.', "", { duration: 50000, clickToClose: true });
          }
        } else {
          showError('Si è verificato un errore durante l\'elaborazione del file.', "", { duration: 50000, clickToClose: true });
        }

      } finally {
      setIsLoading(false);

    }
  };

  // fetchSignedUrl and uploadFile moved to utils/fileUpload.ts

const processFile = async (params: CustomRequestBody) => {
  const response = await apiClient.post(P7M_ENDPOINT, params);

  if (response.download_signed_url) {
    setDownloadUrl(response.download_signed_url);
  }

  if (response.errors && response.errors.length > 0) {
    setErrorFiles(response.errors);
    showWarning(
      `Sono stati trovati ${response.errors.length} file con errori. Puoi scaricarli come elenco.`,
      'Errori rilevati',
      { duration: 50000, clickToClose: true }
    );
  } else {
    setErrorFiles([]);
  }

  return response;
};
  
  const handleDownload = async () => {
    if (!downloadUrl) return; // Verifica se downloadUrl è definito e non vuoto, se non lo è, termina la funzione
    setIsDownloading(true);  // Imposta lo stato di download a true all'inizio dell'operazione di download
    try {
        await downloadFile(downloadUrl, inputFileName.replace(/\.\w+$/, '_decrypted.zip'));
    } catch (error) {
        console.error('Download failed'); // Logga l'errore sulla console se il download fallisce
        alert('Failed to download the file. Please check the file URL and permissions.'); // Mostra un alert all'utente in caso di errore
    } finally {
        setIsDownloading(false); // Reset dello stato di download a false alla fine dell'operazione, indipendentemente dal successo o fallimento
    }
};

const downloadErrorList = () => {
  if (errorFiles.length === 0) return;
  const errorContent = errorFiles.join("\n"); // Crea il contenuto del file
  const blob = new Blob([errorContent], { type: "text/plain" }); // Crea un blob di tipo testo
  const url = window.URL.createObjectURL(blob); // Crea un URL temporaneo
  const safeUrl = sanitizeUrl(url, ['blob:']);
  if (!safeUrl) {
    return;
  }
  // Explicit guard: ensures only blob: URLs are assigned to href, preventing XSS (CWE-80)
  if (!safeUrl.startsWith('blob:')) {
    return;
  }
  const a = document.createElement("a"); // Crea un elemento <a>
  a.href = encodeURI(safeUrl);
  a.download = "errorList.txt"; // Nome del file scaricato
  a.click(); // Simula un click sul link
  window.URL.revokeObjectURL(url); // Libera la memoria
  };

  const [isHovered, setIsHovered] = React.useState(false);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <MainStructure>
      <div className="accordion-wrap">
          <PanelSample config={panelConfig}>
          <div>
          <p>
          Il <strong>Convertitore P7M</strong> è uno strumento di conversione di file cifrati in archivi zip.
          Accetta in input documenti XML/PDF e restituisce come output un file zip contenente i documenti convertiti.
          </p>
          <ul>
            <li>Il servizio è in versione<strong>Beta Test.</strong></li>
            <li>I tempi di attesa sono variabili e possono arrivare fino a diversi minuti per la singola esecuzione.</li>
            <li>La dimensione massima di un singolo file  è di <strong>150Mb</strong></li>
            <li>
              Per feedback contattare: <a href="mailto:it_dig_customsolutions@pwc.com" className="email-link">it_dig_customsolutions@pwc.com</a>
            </li>
            <li>
              <a href="https://pwcita.sharepoint.com/sites/IT-ADV-IT_AST_DIG_PA_COE/Shared%20Documents/Forms/AllItems.aspx?id=%2Fsites%2FIT%2DADV%2DIT%5FAST%5FDIG%5FPA%5FCOE%2FShared%20Documents%2FSviluppi%2F6%2E%20DIG%2FDocument%20Operational%20Toolbox%20%2D%20DOT&viewid=853f9744%2De714%2D4d52%2Db72e%2D8ac987ec1845&p=true&ct=1772100110668&or=OWA%2DNT%2DMail" target="_blank" rel="noopener noreferrer" className="email-link" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                <span className="Appkit4-icon icon-document-text-outline"></span>
                Analisi funzionale
              </a>
            </li>
          </ul>
        </div>
          </PanelSample>
          <AccordionSample items={accordionItems} />
          <Panel
            className="ap-panel"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            style={{ filter: isHovered ? `brightness(90%)` : 'brightness(100%)', padding: '20px', borderRadius: '10px', flex: 1 }}
          >
            <form onSubmit={(e) => {
              e.preventDefault(); // Prevent the default form submission behavior
              handleFileOperations(); // Call the function that handles the file operations
            }}>
              <UploadSample onFileChange={handleFileChange} acceptFileType='.zip' uploadInstruction='La grandezza massima é 150Mb.' />
              <div className="input-p7m">
            </div>
            </form>
            <div>
              <ButtonLoadSubmit
                name="Converti File"
                loading={isLoading}
                onClick={handleFileOperations}
                style={{ width: '250px', marginTop: '20px' }} // Set custom width and margin
              />
            </div>
            {errorFiles.length > 0 && (
              <ButtonLoadSubmit
                name="Download Errori"
                onClick={downloadErrorList}
                style={{ width: '250px', marginTop: '20px' }}
                disabled={errorFiles.length === 0}
                kind='negative'
                icon='Appkit4-icon icon-document-alert-outline'
              />
            )}
            {!isLoading && downloadUrl && (
              <div>
                <ButtonLoadSubmit
                  name={isDownloading ? "Downloading in corso" : "Download"}
                  onClick={handleDownload}
                  style={{ width: '250px', marginTop: '20px' }}
                  disabled={!downloadUrl || isDownloading}
                  icon='Appkit4-icon icon-download-outline'
                  kind='tertiary'
                />
              </div>
            )}
          </Panel>
        </div>
    </MainStructure>
  );
};

export default P7mPage;
